// src/modules/app-views/crm/pages/Kanban/modals/NewOpportunityModal/Step2.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState } from 'react';
import { Col, Form, Row, Switch, Typography } from 'antd';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleTextArea from 'components/SimpleComponents/SimpleTextArea';
import SimplePhone from 'components/SimpleComponents/SimplePhone';
import SimpleEmail from 'components/SimpleComponents/SimpleEmail';
import SimpleUsers from 'components/SimpleComponents/SimpleUsers';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import translateX from 'utils/translateX';

const { Text } = Typography;

const OPPORTUNITY_TYPE_OPTIONS = [
  { value: 'sale', text: translateX('sale') },
  { value: 'partner', text: translateX('partner') },
  { value: 'purchase', text: translateX('purchase') },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const Step2 = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { form } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const phoneValue = Form.useWatch(['phone', 'value'], form);
  const emailValue = Form.useWatch(['email', 'value'], form);
  const hasContact = !!(phoneValue?.trim() || emailValue?.trim());

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Auto-enable company section if AI already extracted company data
  const [showCompany, setShowCompany] = useState(() => !!form.getFieldValue('company')?.trim());

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="p-2">
      {/* ROW 1: Phone & Email */}
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <SimplePhone
            name={['phone', 'value']}
            label="phone"
            international
            required={!hasContact}
          />
        </Col>
        <Col xs={24} sm={12}>
          <SimpleEmail name={['email', 'value']} label="email" required={!hasContact} />
        </Col>
      </Row>

      {/* ROW 2: Contact name & Business area */}
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <SimpleInput name="name" label="contact_name" required />
        </Col>
        <Col xs={24} sm={12}>
          <SimpleInput name="businessAreaRaw" label="business_area" />
        </Col>
      </Row>

      {/* ROW 3: Objective & Opportunity type */}
      <Row gutter={16}>
        <Col xs={24} sm={16}>
          <SimpleInput name="opportunity" label="opportunity_description" required />
        </Col>
        <Col xs={24} sm={8}>
          <SimpleSelect
            name="opportunityType"
            label="opportunity_type"
            options={OPPORTUNITY_TYPE_OPTIONS}
            required
          />
        </Col>
      </Row>

      {/* ROW 4: Note */}
      <Row gutter={16}>
        <Col xs={24}>
          <SimpleTextArea name="note" label="note" rows={2} />
        </Col>
      </Row>

      {/* ROW 5: Company toggle */}
      <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Switch checked={showCompany} onChange={setShowCompany} size="small" />
        </Col>
        <Col flex="auto">
          <Text style={{ marginLeft: 8 }}>{translateX('company')}</Text>
        </Col>
      </Row>

      {/* ROW 6: Company & Job title — only when switch is ON */}
      {showCompany && (
        <Row gutter={16}>
          <Col xs={24} sm={16}>
            <SimpleInput name="company" label="company" required={showCompany} />
          </Col>
          <Col xs={24} sm={8}>
            <SimpleInput name="jobTitleRaw" label="job_title" required={showCompany} />
          </Col>
        </Row>
      )}

      {/* ROW LAST: Responsible consultant (pre-selected with logged user) */}
      <Row gutter={16}>
        <Col xs={24}>
          <SimpleUsers name="consultantId" label="responsible_consultant" />
        </Col>
      </Row>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default Step2;
